import type { AddressParts, GeocodedCoordinates } from "../types";

/**
 * Builds a single address query string from discrete address parts.
 * Parts are joined in street → suburb → city → postal_code → country order,
 * omitting any null or empty values.
 */
export function buildAddressQuery(parts: AddressParts): string {
  return [
    parts.street_address,
    parts.suburb,
    parts.city,
    parts.postal_code,
    parts.country,
  ]
    .filter((part): part is string => Boolean(part?.trim()))
    .join(", ");
}

/**
 * Geocodes an address to longitude/latitude using the Mapbox Geocoding v6 API.
 *
 * Intended for server-side use (e.g. a Next.js Server Action or route handler)
 * so that the access token is not exposed to the browser. Pass the token
 * explicitly rather than relying on a NEXT_PUBLIC_ variable.
 *
 * Returns null when no result is found or the request fails.
 */
export async function geocodeAddress(
  parts: AddressParts,
  accessToken: string,
  options: { country?: string } = {}
): Promise<GeocodedCoordinates | null> {
  const query = buildAddressQuery(parts);
  if (!query) {
    return null;
  }

  const params = new URLSearchParams({
    q: query,
    access_token: accessToken,
    limit: "1",
  });

  if (options.country) {
    params.set("country", options.country);
  }

  try {
    const res = await fetch(
      `https://api.mapbox.com/search/geocode/v6/forward?${params}`
    );

    if (!res.ok) {
      return null;
    }

    const data = await res.json();
    const feature = data?.features?.[0];
    if (!feature) {
      return null;
    }

    const [longitude, latitude] = feature.geometry.coordinates as [
      number,
      number,
    ];
    return { longitude, latitude };
  } catch {
    return null;
  }
}

/**
 * Formats a meeting time range for display.
 * Accepts TIME strings as stored in Postgres (e.g. "18:30:00").
 *
 * @example
 * formatMeetingTime("18:30:00", "20:00:00") // "6:30 PM – 8:00 PM"
 * formatMeetingTime("18:30:00", null)        // "6:30 PM"
 */
export function formatMeetingTime(
  startTime: string | null | undefined,
  endTime: string | null | undefined
): string | null {
  if (!startTime) {
    return null;
  }

  const format = (time: string): string => {
    const [hourStr, minuteStr] = time.split(":");
    const hour = Number(hourStr);
    const minute = Number(minuteStr);
    const period = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    const displayMinute =
      minute === 0 ? "" : `:${String(minute).padStart(2, "0")}`;
    return `${displayHour}${displayMinute} ${period}`;
  };

  const start = format(startTime);
  if (!endTime) {
    return start;
  }
  return `${start} – ${format(endTime)}`;
}

/**
 * Formats discrete address parts into a human-readable string.
 *
 * @example
 * formatAddress({ suburb: "Bellville", city: "Cape Town" }) // "Bellville, Cape Town"
 */
export function formatAddress(parts: AddressParts): string {
  return buildAddressQuery(parts);
}
